import is from '@sindresorhus/is';
import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import { useContext } from 'react';
import type Maybe from 'true-myth/maybe';
import { z } from 'zod';

import { ApplicationContext } from '~/shared/context/context';
import type {
    JobProfileComponent,
    JobProfileDescriptionGenerationResponseContent,
    JobProfileForGenerationWithPrompt,
} from '~/shared/data-contract/job-profile';
import { jobProfileDescriptionGenerationResponseArraySchema } from '~/shared/data-contract/job-profile';
import type { LanguageCodeForJSA } from '~/shared/internationalization';

import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';

type RegenerateParams = {
    readonly acceptLanguage: LanguageCodeForJSA;
    readonly additionalPrompt: Maybe<string>;
    readonly jobProfile: JobProfileComponent & JobProfileForGenerationWithPrompt;
};

export type UseRegenerateJobProfileDescriptionAsyncResult = UseMutationResult<
    readonly JobProfileDescriptionGenerationResponseContent[],
    Error,
    RegenerateParams
>;

const regenerateJobProfileDescriptionEventSchema = z.object({
    id: z.string(),
    payload: z.object({
        content: z.string(),
        status: z.enum(['COMPLETED', 'FAILED']),
    }),
    timestamp: z.string(),
});

export const useRegenerateJobProfileDescriptionAsync = (): UseRegenerateJobProfileDescriptionAsyncResult => {
    const { realtimeEventListenerBuilder } = useContext(ApplicationContext);
    const { regenerateJobProfileDescription } = useApplicationFetchers();

    return useMutation({
        mutationFn: (params: RegenerateParams): Promise<readonly JobProfileDescriptionGenerationResponseContent[]> => {
            return new Promise((resolve, reject) => {
                regenerateJobProfileDescription(params)
                    .then((result) => {
                        if (result.isErr) {
                            reject(result.error);

                            return;
                        }

                        const { streamPath } = result.value;

                        const sse = realtimeEventListenerBuilder.build({
                            autoReconnect: false,
                            method: 'GET',
                            path: `/v1/events/jobprofiles/${streamPath}?lookBackSeconds=5`,
                        });

                        sse.addEventListener('regenerate', (event: MessageEvent) => {
                            const parsedEvent = regenerateJobProfileDescriptionEventSchema.safeParse(
                                JSON.parse(event.data as string),
                            );

                            if (!parsedEvent.success) {
                                sse.stop();
                                reject(new Error('Failed to parse regeneration event'));

                                return;
                            }

                            const { payload } = parsedEvent.data;

                            if (payload.status === 'FAILED') {
                                sse.stop();
                                reject(new Error('Job profile regeneration failed'));

                                return;
                            }

                            const contentParsed = jobProfileDescriptionGenerationResponseArraySchema.safeParse(
                                JSON.parse(payload.content),
                            );

                            if (!contentParsed.success) {
                                sse.stop();
                                reject(new Error('Failed to parse regeneration content'));

                                return;
                            }

                            sse.stop();
                            resolve(contentParsed.data);
                        });

                        sse.addEventListener('error', (event: MessageEvent) => {
                            sse.stop();
                            const errorMessage = event.data as string;
                            reject(new Error(is.nonEmptyString(errorMessage) ? errorMessage : 'SSE connection error'));
                        });

                        sse.addEventListener('close', () => {
                            sse.stop();
                            reject(new Error('SSE connection closed'));
                        });

                        sse.start();
                    })
                    .catch(reject);
            });
        },
    });
};
